import type { Canvas } from 'fabric';
import type { SetStateAction } from 'react';
import { type MutableRefObject, useEffect } from 'react';
import type { SSOImageRepositoryResponse } from '@/shared/api/generated/types/SSOImageRepositoryResponse';
import type { ActiveDevice, ActiveLanguage } from '../store/atoms/editorUiAtom';
import type { SlideData, VariantKey } from '../store/atoms/slideAtom';
import { useJsonData } from './useJsonData';

export interface UseSlideCanvasLoaderParams {
  readonly slideId: number | undefined;
  readonly currentSlide: SSOImageRepositoryResponse | undefined;
  readonly isSlideFetching: boolean;
  readonly activeLanguage: ActiveLanguage;
  readonly activeDevice: ActiveDevice;
  readonly variantKey: VariantKey;
  readonly slide: SlideData;
  readonly slideRef: MutableRefObject<SlideData>;
  readonly getActiveCanvas: () => Canvas | null;
  readonly saveCurrentSlide: (key: VariantKey) => void;
  readonly resetHistory: (key: string) => void;
  readonly pushSnapshot: (key: string) => void;
  readonly setSlide: (update: SetStateAction<SlideData>) => void;
  readonly setCanvasLoadVersion: (update: SetStateAction<number>) => void;
  readonly setActiveCategoryId: (id: number | undefined) => void;
}

/**
 * Carrega o JSON do canvas da variante ativa a partir da `urlStorage` da API e
 * sincroniza nome, categoria e período de exibição vindos do backend.
 *
 * @param params - Dados da API, variante ativa, canvas e setters de estado
 * @returns `isCanvasLoading` — `true` enquanto o JSON é buscado pela 1ª vez
 */
export function useSlideCanvasLoader({
  slideId,
  currentSlide,
  isSlideFetching,
  activeLanguage,
  activeDevice,
  variantKey,
  slide,
  slideRef,
  getActiveCanvas,
  saveCurrentSlide,
  resetHistory,
  pushSnapshot,
  setSlide,
  setCanvasLoadVersion,
  setActiveCategoryId,
}: UseSlideCanvasLoaderParams) {
  // Bloqueia durante o fetch para evitar carregar canvas com urlStorage stale.
  // keepPreviousData retorna dados antigos (com URLs antigas) enquanto refaz o fetch;
  // se carregarmos o canvas a partir dessas URLs, o guard de variante bloqueia o
  // carregamento do conteúdo correto quando os dados frescos chegam.
  const canvasUrl =
    isSlideFetching || (currentSlide?.id != null && currentSlide.id !== slideId)
      ? undefined
      : currentSlide?.ssoImageLangs?.filter(item => item.cultureInfo === activeLanguage)?.[
          activeDevice === 'desktop' ? 0 : 1
        ]?.urlStorage;

  const { data: canvasData, isFetching: isCanvasDataFetching } = useJsonData(canvasUrl);

  // Verdadeiro apenas enquanto o JSON está sendo buscado pela primeira vez para
  // esta variante — não dispara em trocas de viewport quando já há dados no átomo.
  const currentVariantHasCanvas = !!slide.variants[variantKey]?.canvas;
  const isCanvasLoading =
    !!slideId && (isSlideFetching || (isCanvasDataFetching && !currentVariantHasCanvas));

  useEffect(() => {
    if (!canvasData) return;
    const canvas = getActiveCanvas();
    if (!canvas) return;
    // Não sobrescrever variante que já foi carregada ou editada nesta sessão.
    // Sem este guard, trocar de viewport faz o TanStack Query retornar o JSON
    // da API do cache, disparando o effect novamente e apagando as edições.
    if (slideRef.current.variants[variantKey]?.canvas) return;
    // `videoSrc` é um campo customizado no topo do JSON (ignorado pelo Fabric).
    // biome-ignore lint/complexity/useLiteralKeys: acesso por índice exigido pelo TS (noPropertyAccessFromIndexSignature)
    const rawVideoSrc = canvasData['videoSrc'];
    const videoSrc = typeof rawVideoSrc === 'string' ? rawVideoSrc : undefined;
    void canvas.loadFromJSON(canvasData).then(() => {
      // Com vídeo de fundo, o canvas precisa ser transparente para o <video> aparecer atrás.
      if (videoSrc) canvas.backgroundColor = 'transparent';
      canvas.requestRenderAll();
      saveCurrentSlide(variantKey); // sincroniza slideAtom → dispara re-render do PreviewTab
      if (videoSrc) {
        setSlide(prev => ({
          ...prev,
          variants: {
            ...prev.variants,
            [variantKey]: { ...prev.variants[variantKey], backgroundVideo: videoSrc },
          },
        }));
      }
      resetHistory(variantKey);
      pushSnapshot(variantKey);
      setCanvasLoadVersion(v => v + 1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canvasData,
    getActiveCanvas,
    resetHistory,
    variantKey,
    saveCurrentSlide,
    pushSnapshot,
    setSlide,
  ]);

  // Sincroniza nome, categoria e período de exibição a partir dos dados da API
  useEffect(() => {
    if (!slideId || !currentSlide) return;
    // Garante que o categoryId local sempre reflita o valor persistido no backend,
    // independente do parâmetro de URL (que não é atualizado ao mudar de categoria)
    if (currentSlide.categoryId) setActiveCategoryId(currentSlide.categoryId as number);
    setSlide(prev => {
      const period: { startDate?: string; endDate?: string } = {};
      // Extrai apenas a parte da data (YYYY-MM-DD) para compatibilidade com <input type="date">
      if (currentSlide.dateStart) {
        const startDatePart = currentSlide.dateStart.split('T')[0];
        if (startDatePart) period.startDate = startDatePart;
      }
      if (currentSlide.dateEnd) {
        const endDatePart = currentSlide.dateEnd.split('T')[0];
        if (endDatePart) period.endDate = endDatePart;
      }
      return {
        ...prev,
        id: currentSlide.id ?? prev.id,
        name: currentSlide.title ?? prev.name,
        ...(Object.keys(period).length > 0 ? { displayPeriod: period } : {}),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideId, currentSlide, setSlide]);

  return { isCanvasLoading } as const;
}
